/**
 * modalStack.ts
 * 글로벌 모달 스택 — Android 뒤로가기 가드(BackGuard) 에서 최상위 모달 닫기에 사용.
 * 각 모달은 mount 시 자기 close 함수를 등록하고, unmount 시 해제.
 */

type CloseFn = () => void;

const stack: CloseFn[] = [];

/** 모달 mount 시 호출. 반환된 함수를 unmount 시 호출. */
export function registerModalClose(fn: CloseFn): () => void {
  stack.push(fn);
  return () => {
    const i = stack.lastIndexOf(fn);
    if (i >= 0) stack.splice(i, 1);
  };
}

/** 최상위 모달 닫기. 닫은 게 있으면 true. */
export function closeTopModal(): boolean {
  const fn = stack.pop();
  if (!fn) return false;
  try { fn(); } catch { /* silent */ }
  return true;
}

export function hasOpenModal(): boolean {
  return stack.length > 0;
}

/**
 * 모달이 네이티브 뒤로가기(popstate)가 아닌 다른 방식(UI 버튼/오버레이 클릭/Esc 등)으로
 * 닫힐 때, 그 모달이 열릴 때 push 했던 history entry를 정리(history.back())하기 위해
 * 스스로 발생시키는 popstate를 BackGuard가 무시하도록 알리는 1회성 플래그.
 * (그렇지 않으면 그 정리용 popstate가 "홈에서 뒤로가기"로 오인되어 종료 토스트가
 * 의도치 않게 뜨거나, 가드 상태가 어긋나는 문제 발생 — #449 관련)
 */
let suppressNextPop = false;

export function suppressNextPopstate(): void {
  suppressNextPop = true;
}

export function consumeSuppressFlag(): boolean {
  const v = suppressNextPop;
  suppressNextPop = false;
  return v;
}

/**
 * #449: 뒤로가기 가드/모달이 심는 history entry는 지금까지 전부 URL이 동일(state만 다름)했다.
 * Android Chrome의 시스템 뒤로가기 제스처는 URL이 안 바뀌는 연속된 entry를 한 번에 여러 개
 * 건너뛰는 것으로 관찰됨(popstate 자체가 안 잡히고 여러 칸 앞의 페이지로 바로 이동) — 이게
 * 종료 확인 토스트가 씹히던 근본 원인. 매 push마다 다른 해시를 붙여 URL을 구분되게 만들어
 * 연속 entry가 "동일 페이지"로 뭉뚱그려 스킵되지 않도록 한다.
 */
let guardEntrySeq = 0;
export function nextGuardHash(): string {
  guardEntrySeq += 1;
  return `#_g${guardEntrySeq}`;
}

/**
 * #449: 최신 크롬(안드로이드)의 예측 뒤로가기 제스처는 같은 문서 안에서 쌓은 history entry를
 * popstate 없이 건너뛰어 버리는 것으로 확인됨(해시로 entry를 구분해도 동일) — history depth를
 * 쌓는 방식 자체가 근본적으로 안 맞음. Navigation API(`window.navigation`)가 있으면 그 쪽으로
 * 완전히 대체하고, 없는 구형 브라우저에서만 기존 history sentinel 방식을 유지한다.
 */
export function hasNavigationApi(): boolean {
  return typeof window !== "undefined" && !!window.navigation;
}
